// QuickNotesColorPalette.js - Color palette utility for consistent Activity Type colors across Quick Notes reports

// Base color palette inspired by the rjTheme graph colors with additional accessible colors (pastel versions)
const baseColors = [
  '#A5C9E4', // Pastel blue
  '#8BB4D9', // Pastel dark blue
  '#B8BFBC', // Pastel gray
  '#9DA5B2', // Pastel dark gray
  '#F5A8D3', // Pastel magenta
  '#B491BD', // Pastel purple
  '#80DEF9', // Pastel cyan
  '#80D9CA', // Pastel teal
  '#8099C7', // Pastel navy
  '#FFC680', // Pastel orange
  '#94D3A2', // Pastel green
  '#EE9BA2', // Pastel red
  '#B7A1E0', // Pastel violet
  '#FEBF8A', // Pastel orange-red
  '#90E4CB', // Pastel mint
  '#F4A1C6', // Pastel pink
  '#B388F9', // Pastel indigo
  '#8BD1DC', // Pastel info blue
  '#FFE083', // Pastel yellow
  '#A1A7AA', // Pastel dark
];

// Additional colors for overflow (pastel variations)
const overflowColors = [
  '#BDD3E8', '#8DA7C1', '#BDBEBC', '#A5AAB2', '#F89ED8',
  '#C4A1CB', '#99E4FA', '#99E1D3', '#99A2D1', '#FFD599',
  '#A9E1B3', '#F2ADAD', '#C7AFE8', '#FEC6A3', '#A9EAD5',
  '#F6B4D0', '#C499FA', '#A5E4E8', '#FFE999', '#ADB0B4',
];

// Planned vs Unplanned colors (distinct from activity type colors) - pastel versions
const plannedColors = {
  planned: '#94D3A2',     // Pastel green for planned activities
  unplanned: '#FFE083',   // Pastel yellow/amber for unplanned activities
};

// Maximum contrast color palette - each color from completely different color family (pastel versions)
const categoryColorPalette = [
  '#FFB3B3', // Pastel red - primary red
  '#B3B3FF', // Pastel blue - primary blue  
  '#B3FFB3', // Pastel green - primary green
  '#FFFFB3', // Pastel yellow - primary yellow
  '#FFB3FF', // Pastel magenta - primary magenta
  '#B3FFFF', // Pastel cyan - primary cyan
  '#FFD9B3', // Pastel orange - distinct orange
  '#D9B3FF', // Pastel purple - distinct purple
  '#B3B3B3', // Pastel gray - softer than black
  '#D0D0D0', // Light gray - neutral tone
  '#D9B3B3', // Pastel maroon - dark red family
  '#B3B3D9', // Pastel navy - dark blue family
  '#B3D9B3', // Pastel green - dark green family
  '#D9D9B3', // Pastel olive - dark yellow family
  '#D9B3D9', // Pastel purple - dark magenta family
  '#B3D9D9', // Pastel teal - dark cyan family
  '#E0E0E0', // Light silver - light gray
  '#FFD1B3', // Pastel orange red - warm tone
  '#D1B3FF', // Pastel blue violet - cool tone
  '#D1FFB3', // Pastel yellow green - nature tone
  '#FFB3D1', // Pastel rose - pink family
  '#B3D1FF', // Pastel azure - sky blue
  '#FFD1B3', // Pastel burnt orange - earth tone
  '#CCB3E6', // Pastel dark violet - royal tone
  '#B3E6D9', // Pastel turquoise - aqua family
];

// Explicit color mapping using completely different color families (pastel versions)
const categoryColorMap = {
  // Activity Type Categories (these match what appears in the chart)
  'Meeting': '#B3B3FF',             // Pastel blue - meeting activities
  'Note': '#B3FFB3',                // Pastel green - note-taking activities  
  'Email': '#FFB3B3',               // Pastel red - email communication
  'Phone Call': '#FFB3FF',          // Pastel magenta - phone communication
  'Call': '#FFB3FF',                // Pastel magenta - alternate name for phone
  
  // Business/Professional Categories
  'Client Meeting': '#B3B3FF',      // Pastel blue - important client interaction
  'Internal Meeting': '#B3FFFF',    // Pastel cyan - team collaboration
  'Sales Call': '#FFD9B3',          // Pastel orange - dynamic sales energy
  'Follow-up': '#FFFFB3',           // Pastel yellow - nurturing relationships
  'Consultation': '#D9B3FF',        // Pastel purple - expert advice
  'Presentation': '#D0D0D0',        // Light gray - engaging communication
  'Training': '#D9B3B3',            // Pastel maroon - learning and growth
  'Planning': '#B3B3D9',            // Pastel navy - strategic thinking
  
  // Administrative Categories
  'Documentation': '#B3D9B3',       // Pastel dark green - organized information
  'Research': '#B3B3D9',            // Pastel navy - thorough investigation
  'Review': '#D9B3B3',              // Pastel maroon - careful evaluation
  'Compliance': '#D9B3D9',          // Pastel dark purple - regulatory importance
  'Reporting': '#B3D9D9',           // Pastel teal - systematic tracking
  
  // Communication Categories
  'Email': '#C8E6C8',               // Pastel lime green - quick communication
  'Phone Call': '#E8C1C1',          // Pastel crimson - personal connection
  'Video Conference': '#B3E7E8',    // Pastel dark turquoise - modern technology
  'Networking': '#EDB8EB',          // Pastel orchid - relationship building
  
  // Project Categories
  'Project Work': '#D1B3FF',        // Pastel blue violet - focused productivity
  'Development': '#CCB3E6',         // Pastel dark violet - innovation and growth
  'Testing': '#B3D1FF',             // Pastel azure - quality assurance
  
  // Default/General Categories
  'General': '#D0D0D0',             // Light gray - versatile default
  'Other': '#B3B3B3',               // Pastel gray - catch-all category
  'Miscellaneous': '#E0E0E0',       // Light silver - varied activities
};

// Category-specific colors (will be populated dynamically)
const categoryColors = {};

// Keep track of assigned category colors for round-robin assignment
let categoryColorIndex = 0;

// Function to reset color assignments (useful for debugging)
const resetColorAssignments = () => {
  Object.keys(categoryColors).forEach(key => delete categoryColors[key]);
  categoryColorIndex = 0;
  console.log('Color assignments reset');
};

// Explicit color mapping for common activity types to ensure distinct, recognizable colors (pastel versions)
const activityTypeColorMap = {
  'Meeting': '#B3B3FF',    // Pastel blue - professional meeting color
  'Note': '#B3FFB3',       // Pastel green - note-taking/writing color  
  'Email': '#FFB3B3',      // Pastel red - communication/email color
  'Call': '#FFB3FF',       // Pastel magenta - phone/call color
  'Phone Call': '#FFB3FF', // Pastel magenta - phone/call color (alternate name)
  'Task': '#FFB3FF',       // Pastel magenta - task/action color
  'Follow-up': '#B3FFFF',  // Pastel cyan - follow-up color
  'Research': '#D9B3D9',   // Pastel purple - research/analysis color
  'Planning': '#FFD9B3',   // Pastel orange - planning/strategy color
};

/**
 * Generates a color for an activity type based on its name
 * Uses explicit mapping for common types, falls back to hash function for others
 * @param {string} activityTypeName - The name of the activity type
 * @returns {string} - Hex color code
 */
const getActivityTypeColor = (activityTypeName) => {
  if (!activityTypeName) return baseColors[0];
  
  // Check for explicit mapping first
  if (activityTypeColorMap[activityTypeName]) {
    return activityTypeColorMap[activityTypeName];
  }
  
  // Fall back to hash function for unmapped activity types
  let hash = 0;
  for (let i = 0; i < activityTypeName.length; i++) {
    const char = activityTypeName.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Ensure positive index, skip colors already used in explicit mapping
  const usedColors = Object.values(activityTypeColorMap);
  const availableColors = [...baseColors, ...overflowColors].filter(color => !usedColors.includes(color));
  const index = Math.abs(hash) % availableColors.length;
  
  return availableColors[index] || baseColors[0];
};

/**
 * Gets color for planned vs unplanned status
 * @param {boolean} isPlanned - Whether the activity is planned
 * @returns {string} - Hex color code
 */
const getPlannedStatusColor = (isPlanned) => {
  return isPlanned ? plannedColors.planned : plannedColors.unplanned;
};

/**
 * Gets color for a category based on its name
 * Uses explicit mapping for common categories, falls back to enhanced palette for others
 * @param {string} categoryName - The name of the category
 * @returns {string} - Hex color code
 */
const getCategoryColor = (categoryName) => {
  if (!categoryName) {
    return categoryColorPalette[0];
  }
  
  // Check for explicit mapping first
  if (categoryColorMap[categoryName]) {
    return categoryColorMap[categoryName];
  }
  
  // Check if we've already assigned a color to this category
  if (categoryColors[categoryName]) {
    return categoryColors[categoryName];
  }
  
  // Use aggressive distribution to maximize contrast between consecutive colors
  // Jump around the palette to ensure maximum visual separation
  const paletteLength = categoryColorPalette.length;
  
  // Use a larger prime number step for maximum color family separation
  // This ensures we cycle through completely different color families
  const step = 11; // Larger prime for maximum separation
  const colorIndex = (categoryColorIndex * step) % paletteLength;
  const color = categoryColorPalette[colorIndex];
  categoryColorIndex++;
  
  // Cache the color for this category
  categoryColors[categoryName] = color;
  
  return color;
};

/**
 * Gets an array of colors for multiple activity types
 * Useful for creating legends and ensuring consistent colors across charts
 * @param {Array<string>} activityTypeNames - Array of activity type names
 * @returns {Array<string>} - Array of hex color codes
 */
const getActivityTypeColors = (activityTypeNames) => {
  if (!Array.isArray(activityTypeNames)) return [];
  return activityTypeNames.map(name => getActivityTypeColor(name));
};

/**
 * Gets color based on stack dimension and key
 * @param {string} stackDimension - The stack dimension (activity_type, planned, category)
 * @param {string} stackKey - The specific key value
 * @returns {string} - Hex color code
 */
const getStackColor = (stackDimension, stackKey) => {
  // Normalize stack dimension to lowercase for consistent matching
  const normalizedDimension = stackDimension?.toLowerCase();
  
  switch (normalizedDimension) {
    case 'activity_type':
      return getActivityTypeColor(stackKey);
    case 'planned':
      return getPlannedStatusColor(stackKey === 'Planned');
    case 'category':
      return getCategoryColor(stackKey);
    default:
      return baseColors[0];
  }
};

/**
 * Creates a color map for chart data
 * @param {Array} data - Chart data with stack keys
 * @param {string} stackDimension - The stack dimension
 * @returns {Object} - Map of stack key to color
 */
const createColorMap = (data, stackDimension) => {
  const colorMap = {};
  const uniqueKeys = new Set();
  
  // Collect all unique stack keys from the data
  data.forEach(item => {
    if (item.buckets && Array.isArray(item.buckets)) {
      item.buckets.forEach(bucket => {
        uniqueKeys.add(bucket.key);
      });
    }
  });
  
  // Assign colors to each unique key
  Array.from(uniqueKeys).forEach(key => {
    colorMap[key] = getStackColor(stackDimension, key);
  });
  return colorMap;
};

/**
 * Gets lighter version of a color for hover states
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to lighten (0-100)
 * @returns {string} - Lighter hex color code
 */
const lightenColor = (color, amount = 20) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

/**
 * Gets darker version of a color for active states
 * @param {string} color - Hex color code
 * @param {number} amount - Amount to darken (0-100)
 * @returns {string} - Darker hex color code
 */
const darkenColor = (color, amount = 20) => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  
  return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
    (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
    (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
};

// Export the utility functions
export {
  getActivityTypeColor,
  getPlannedStatusColor,
  getCategoryColor,
  getActivityTypeColors,
  getStackColor,
  createColorMap,
  lightenColor,
  darkenColor,
  resetColorAssignments,
  baseColors,
  plannedColors,
};

// Default export with all functions
export default {
  getActivityTypeColor,
  getPlannedStatusColor,
  getCategoryColor,
  getActivityTypeColors,
  getStackColor,
  createColorMap,
  lightenColor,
  darkenColor,
  colors: {
    base: baseColors,
    planned: plannedColors,
  },
};